import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { PersonalInfo } from '../types/on-boarding.type';
import CustomSelect from 'components/Form/CustomSelect';
import { GenderOption, MaritalStatus } from 'constants/marital-status';
import { Icon, Icons } from 'components/Icon';
import { usePersonalinfoUpdate } from 'common/queries-and-mutations/user';
import { selectAccountDetails } from 'selectors/account-selector';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { STEPS } from 'constants/steps';
import { useNavigate } from 'react-router-dom';
import { findNextPage } from 'utils';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';

export default function BasicInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);
  const { mutate, isSuccess, isError } = usePersonalinfoUpdate();
  const { user } = useAppSelector(selectAccountDetails);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const formik = useFormik({
    initialValues: {
      homeAddress: user?.homeAddress,
      gender: user?.gender,
      maritalStatus: user?.maritalStatus,
      dateOfBirth: user?.dateOfBirth
    },
    validationSchema: Yup.object({
      homeAddress: Yup.string().required('Home address is required').nullable(),
      gender: Yup.string().required('Gender is required').nullable(),
      maritalStatus: Yup.string()
        .required('Marital status is required')
        .nullable(),
      dateOfBirth: Yup.date().required('Date of birth is required').nullable()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBasicInfoSubmit(values);
    }
  });

  const onBasicInfoSubmit = (formValue: PersonalInfo) => {
    //Total percentage of first step should 45 while the second step will 40 and the last one will be 15
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      dispatch(fetchUserInformation());
      setIsSubmitting(false);
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.PERSONAL_INFORMATION));
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [dispatch, isError, isSuccess, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return false;
    }
    mutate(formik.values);
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={3}
        stepPercentage={STEPS.PERSONAL_INFORMATION}
        timelineStep={1}
      />

      <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
        Personal Information
      </p>
      <form
        autoComplete="false"
        className="m-10"
        onSubmit={formik.handleSubmit}
      >
        <div className="grid md:grid-cols-2 md:gap-6">
          <div className="group relative z-0 mb-6 w-full">
            <TextField
              name="homeAddress"
              placeholder=""
              value={formik.values.homeAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Home Address"
              error={
                formik.touched.homeAddress ? formik.errors.homeAddress : ''
              }
              isRequired
              size="lg"
              className="rounded-2xl border-[1.5px] border-gray-300"
            />
          </div>
          <div className="group relative z-0 mb-6 w-full">
            <CustomSelect
              options={GenderOption}
              selectedOption={{
                value: formik.values.gender,
                label: formik.values.gender
              }}
              handleOptionChange={option => {
                formik.setFieldValue('gender', option?.value);
              }}
              label="Gender"
              name="gender"
              isRequired
              isSearchable
              className="h-[3.4rem] rounded-2xl"
              error={formik.touched.gender ? formik.errors.gender : ''}
            />
          </div>
        </div>
        <div className="grid md:grid-cols-2 md:gap-6">
          <div className="group relative z-0 mb-6 w-full">
            <CustomSelect
              options={MaritalStatus}
              selectedOption={{
                value: formik.values.maritalStatus,
                label: formik.values.maritalStatus
              }}
              handleOptionChange={option => {
                formik.setFieldValue('maritalStatus', option?.value);
              }}
              label="Marital Status"
              name="maritalStatus"
              isRequired
              isSearchable
              className="h-[3.4rem] rounded-2xl"
              error={
                formik.touched.maritalStatus ? formik.errors.maritalStatus : ''
              }
            />
          </div>
          <div className="group relative z-0 mb-6 w-full">
            <TextField
              name="dateOfBirth"
              placeholder=""
              value={formik.values.dateOfBirth}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Date of Birth"
              error={
                formik.touched.dateOfBirth ? formik.errors.dateOfBirth : ''
              }
              isRequired
              size="lg"
              type="date"
              className="rounded-2xl border-[1.5px] border-gray-300"
            />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2">
          <Icon name={Icons.Required} />
          <span className="text-[10px] text-[#7A7C87]">Indicates Required</span>
        </div>

        <div className="mt-10 flex items-end justify-end gap-8">
          {parentPath !== 'dashboard' && (
            <button
              type="submit"
              className="font-circular text-[15px] font-bold text-[#353535] underline"
              onClick={() => {
                onContinueLater();
                setIsLater(true);
              }}
            >
              Save and Continue Later
            </button>
          )}
          <Button
            variant="primary"
            size="md"
            className="mt-5 w-[7rem] rounded p-[0.1rem]"
            label="Proceed"
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          ></Button>
        </div>
      </form>
    </div>
  );
}
